/**
 * 🆕 [Phase 72 — 2026-06-21] PermisConduirePhotosBlock
 *
 * Bloc UI affiché dans l'onglet « Permis » de la fiche employé, qui permet
 * d'uploader, visualiser et remplacer les photos RECTO et VERSO du permis
 * de conduire d'un employé.
 *
 * Backend :
 *   POST   /api/employees/{id}/upload-photo-permis-conduire/{recto|verso}
 *   DELETE /api/employees/{id}/photo-permis-conduire/{recto|verso}
 *
 * Storage : `storage_service.upload_file` → ObjStore Emergent avec fallback
 * automatique vers GridFS (Phase 68 hotfix, l'utilisatrice n'est jamais bloquée
 * quand l'ObjStore est down/HTTP 402).
 *
 * Stockage DB sur `employees` :
 *   - permis_photo_recto_url
 *   - permis_photo_verso_url
 *
 * Limites client :
 *   - Max 10 MB
 *   - Formats JPEG, PNG, WebP, HEIC, HEIF
 *
 * Visualisation : clic sur la miniature → modal plein écran (zoom max).
 */
import { useRef, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { Camera, Upload, Trash2, ZoomIn, RefreshCw, IdCard } from 'lucide-react';
import { toast } from 'sonner';
import { urlFichier } from '@/lib/fichiers';

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';
const ALLOWED_EXTS = ['jpg', 'jpeg', 'png', 'webp', 'heic', 'heif'];
const MAX_BYTES = 10 * 1024 * 1024;

function PermisCote({ employee, cote, onUpdate, canEdit, onZoom }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const field = `permis_photo_${cote}_url`;
  const photoUrl = employee?.[field];
  const label = cote === 'recto' ? 'Recto' : 'Verso';

  const handlePick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    const ext = (file.name || '').split('.').pop().toLowerCase();
    if (!ALLOWED_EXTS.includes(ext)) {
      toast.error(`Format .${ext} non supporté. Acceptés : ${ALLOWED_EXTS.join(', ')}`);
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error(`Fichier trop volumineux (${(file.size / 1024 / 1024).toFixed(1)} MB). Max 10 MB.`);
      return;
    }
    void handleUpload(file);
  };

  const handleUpload = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(
        `${API_URL}/api/employees/${employee.id}/upload-photo-permis-conduire/${cote}`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success(`Photo ${label.toLowerCase()} enregistrée`);
      if (onUpdate) onUpdate();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Erreur lors de l\'upload';
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Supprimer la photo ${label.toLowerCase()} du permis ?`)) return;
    setDeleting(true);
    try {
      await axios.delete(
        `${API_URL}/api/employees/${employee.id}/photo-permis-conduire/${cote}`
      );
      toast.success(`Photo ${label.toLowerCase()} supprimée`);
      if (onUpdate) onUpdate();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Erreur lors de la suppression';
      toast.error(msg);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div
      className="flex flex-col gap-2 p-3 border rounded-lg bg-white"
      data-testid={`permis-conduire-${cote}-block`}
    >
      <div className="flex items-center justify-between">
        <Badge variant="outline" className="text-xs font-semibold uppercase tracking-wide">
          {label}
        </Badge>
        {photoUrl && <span className="text-[10px] text-emerald-600">✓ Enregistrée</span>}
      </div>

      {photoUrl ? (
        <div className="relative group">
          <button
            type="button"
            onClick={() => onZoom?.({ url: photoUrl, cote, label })}
            className="block w-full aspect-[1.6/1] rounded-md overflow-hidden bg-slate-100 border hover:ring-2 hover:ring-amber-400 transition"
            data-testid={`permis-conduire-${cote}-thumb`}
          >
            <img
              src={urlFichier(photoUrl)}
              alt={`Permis ${label}`}
              className="w-full h-full object-contain"
              loading="lazy"
            />
            <div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 group-hover:opacity-100 transition pointer-events-none">
              <ZoomIn className="w-6 h-6 text-white" />
            </div>
          </button>
          {canEdit && (
            <div className="flex gap-1 mt-2">
              <Button
                size="sm"
                variant="outline"
                className="flex-1 text-xs"
                onClick={() => inputRef.current?.click()}
                disabled={uploading}
                data-testid={`permis-conduire-${cote}-replace-btn`}
              >
                {uploading ? <RefreshCw className="w-3 h-3 animate-spin mr-1" /> : <Upload className="w-3 h-3 mr-1" />}
                Remplacer
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="text-red-600 border-red-200 hover:bg-red-50"
                onClick={handleDelete}
                disabled={deleting}
                data-testid={`permis-conduire-${cote}-delete-btn`}
              >
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
          )}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={!canEdit || uploading}
          className={`aspect-[1.6/1] w-full rounded-md border-2 border-dashed flex flex-col items-center justify-center gap-1 transition ${
            canEdit
              ? 'border-amber-300 bg-amber-50/40 hover:bg-amber-100/60 hover:border-amber-400 cursor-pointer'
              : 'border-slate-200 bg-slate-50 cursor-not-allowed opacity-60'
          }`}
          data-testid={`permis-conduire-${cote}-upload-btn`}
        >
          {uploading ? (
            <>
              <RefreshCw className="w-5 h-5 text-amber-600 animate-spin" />
              <span className="text-xs text-amber-700">Envoi en cours…</span>
            </>
          ) : (
            <>
              <Camera className="w-6 h-6 text-amber-600" />
              <span className="text-xs font-medium text-amber-700">Ajouter photo {label.toLowerCase()}</span>
              <span className="text-[10px] text-amber-600/80">JPEG, PNG, WebP, HEIC — max 10 MB</span>
            </>
          )}
        </button>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif"
        onChange={handlePick}
        className="hidden"
        data-testid={`permis-conduire-${cote}-file-input`}
      />
    </div>
  );
}

export function PermisConduirePhotosBlock({ employee, onUpdate, canEdit = true }) {
  const [zoomImg, setZoomImg] = useState(null);

  return (
    <Card data-testid="permis-conduire-photos-block">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <IdCard className="h-5 w-5 text-amber-600" />
          Photos du permis de conduire
        </CardTitle>
        <p className="text-xs text-slate-500">
          Téléversez le recto et le verso du permis pour archivage. Stocké de façon sécurisée.
        </p>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <PermisCote
            employee={employee}
            cote="recto"
            canEdit={canEdit}
            onUpdate={onUpdate}
            onZoom={setZoomImg}
          />
          <PermisCote
            employee={employee}
            cote="verso"
            canEdit={canEdit}
            onUpdate={onUpdate}
            onZoom={setZoomImg}
          />
        </div>
      </CardContent>

      {/* Viewer modal plein écran */}
      <Dialog open={!!zoomImg} onOpenChange={(open) => !open && setZoomImg(null)}>
        <DialogContent
          className="max-w-5xl max-h-[90vh] p-2 bg-slate-900 border-slate-700"
          data-testid="permis-conduire-zoom-dialog"
        >
          {zoomImg && (
            <div className="flex flex-col items-center gap-3">
              <p className="text-sm text-slate-200 font-semibold">
                Permis — {zoomImg.label}
              </p>
              <img
                src={urlFichier(zoomImg.url)}
                alt={`Permis ${zoomImg.label} plein écran`}
                className="max-w-full max-h-[80vh] object-contain rounded"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export default PermisConduirePhotosBlock;
